import { api } from '../../../shared/api/client';
import {
  normalizeListEnvelope,
  toGenexusParams,
  type ListEnvelope,
} from '../../../shared/api/genexus-query';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type {
  ManualRequisicaoCompra,
  NotaFiscalColabPendenteItem,
  NotaMaeOption,
  PreviewAutomaticaInput,
  PreviewAutomaticaLinha,
} from './types';

/**
 * Grid de pendências de faturamento — agnóstico de empresa por definição do
 * webpanel `EmpresaGerarNotaFiscalColaborador` (For each sem filtro por
 * empresa). Genie ATD-0028 / card [m1O7KeaU]: API dedicada
 * `ApiNotaFiscalColaboradorPendente`. Adapter do BFF converte numeric strings
 * do wire (ex.: "1158.90") em `number`.
 *
 * `pagina`/`porPagina`/`busca` são honrados upstream. `ordenarPor`/`ordem` são
 * enviados mas hoje **ignorados** pela Object API (probe 2026-07-21: qualquer
 * campo/direção devolve sempre a ordem default) — card aberto pra Genie.
 */
export async function listPendentesFaturamento(
  query: DataTableQuery,
): Promise<ListEnvelope<NotaFiscalColabPendenteItem>> {
  const { data } = await api.get<{
    data: NotaFiscalColabPendenteItem[];
    meta: { total: number; pagina?: number; porPagina?: number };
  }>('/v1/notas-fiscais-colaborador-pendentes', { params: toGenexusParams(query) });
  return normalizeListEnvelope(data);
}

/**
 * Combo dinâmico de Nota-Mãe — sub-recurso de company via `extraGetRoutes`.
 * Filtro upstream: `NotaMae=true`, status EMI, `NotaMaeSaldo > 0` (LoadDVCombo
 * do WWP). Sem paginação — universo pequeno por empresa.
 */
export async function listNotasMae(companyId: number): Promise<NotaMaeOption[]> {
  const { data } = await api.get<{ data: NotaMaeOption[] }>(
    `/v1/companies/${companyId}/notas-fiscais/notas-mae`,
  );
  return data.data;
}

/**
 * Preview do espelho (Fase 2) — retorna as linhas que serão emitidas
 * (`emissaoOK=true`) + as residuais (`emissaoOK=false`, sem RC cobrindo).
 */
export async function previewAutomatica(
  companyId: number,
  input: PreviewAutomaticaInput,
): Promise<PreviewAutomaticaLinha[]> {
  const { data } = await api.post<{ data: PreviewAutomaticaLinha[] }>(
    `/v1/companies/${companyId}/notas-fiscais/preview-automatica`,
    input,
  );
  return data.data;
}

/**
 * Body do gerar-automatica breaking (Fase 2). `requisicoes[]` carrega só as
 * linhas com `emissaoOK=true` do preview.
 */
export interface GerarAutomaticaInput extends PreviewAutomaticaInput {
  requisicoes: { requisicaoCompraCodigo: number; valor: number }[];
}

/**
 * Emite N NFs Empresa (uma por RC em `requisicoes[]`). Retorna array das NFs
 * criadas. Enquanto Genie não publicar, mock devolve array vazio simulando
 * sucesso — smoke-browser cobre o fluxo até o toast.
 */
export async function gerarAutomaticaBatch(
  companyId: number,
  input: GerarAutomaticaInput,
): Promise<{ emitidas: number }> {
  const { data } = await api.post<{ data: unknown[] }>(
    `/v1/companies/${companyId}/notas-fiscais/gerar-automatica`,
    input,
  );
  return { emitidas: data.data.length };
}

/**
 * Lista RCs disponíveis pra alocação manual — filtradas pelo cliente da NF
 * Colab, com saldo residual > 0 e (opcional) restrição da nota-mãe.
 */
export interface ListManualRcsQuery {
  colaboradorCodigo: number;
  colaboradorNotaFiscalCodigo: number;
  notaMaeEmpresaNotaFiscalCodigo?: number | null;
}

export async function listManualRequisicoesCompra(
  companyId: number,
  query: ListManualRcsQuery,
): Promise<ManualRequisicaoCompra[]> {
  const params: Record<string, string> = {
    colaboradorCodigo: String(query.colaboradorCodigo),
    colaboradorNotaFiscalCodigo: String(query.colaboradorNotaFiscalCodigo),
  };
  if (query.notaMaeEmpresaNotaFiscalCodigo)
    params.notaMaeEmpresaNotaFiscalCodigo = String(query.notaMaeEmpresaNotaFiscalCodigo);
  const { data } = await api.get<{ data: ManualRequisicaoCompra[] }>(
    `/v1/companies/${companyId}/notas-fiscais/manual-requisicoes-compra`,
    { params },
  );
  return data.data;
}

/**
 * Body do gerar-manual breaking (Fase 3). `requisicoes[]` só carrega RCs
 * marcadas `emissaoOK=true` com `valor > 0` na UI.
 */
export interface GerarManualInput extends PreviewAutomaticaInput {
  requisicoes: { requisicaoCompraCodigo: number; valor: number }[];
}

export async function gerarManualBatch(
  companyId: number,
  input: GerarManualInput,
): Promise<{ emitidas: number }> {
  const { data } = await api.post<{ data: unknown[] }>(
    `/v1/companies/${companyId}/notas-fiscais/gerar-manual`,
    input,
  );
  return { emitidas: data.data.length };
}
